<template>
  <div class="user-view-page">
    <div
      v-if="loading"
      class="user-view-page__state text-center"
    >
      <b-spinner variant="primary" label="Loading user" />
      <p class="text-muted mt-1 mb-0">Loading user profile…</p>
    </div>

    <b-alert
      v-else-if="loadError"
      show
      variant="danger"
      class="mb-1"
    >
      <h4 class="alert-heading">
        Could not load user
      </h4>
      <div class="alert-body">
        {{ loadError }}
        <b-link
          class="alert-link d-block mt-50"
          :to="{ name: 'apps-users-list' }"
        >
          Back to User List
        </b-link>
      </div>
    </b-alert>

    <b-alert
      v-else-if="userData === undefined"
      show
      variant="danger"
      class="mb-1"
    >
      <h4 class="alert-heading">
        User not found
      </h4>
      <div class="alert-body">
        No user found for
        <code>{{ routeKey }}</code>.
        Check the
        <b-link
          class="alert-link"
          :to="{ name: 'apps-users-list' }"
        >
          User List
        </b-link>.
      </div>
    </b-alert>

  </div>
</template>

<script>
import {
  BAlert, BLink, BSpinner,
} from 'bootstrap-vue'
import userService from '@/services/user-service'
import { fetchOrgChartUsers } from '@/services/org-chart-service'
import { normalizeId } from '@/utils/manager-id-matcher'

function extractUserArray(res) {
  if (!res) return []
  if (Array.isArray(res)) return res
  if (res.status === 200 && res.data) {
    if (Array.isArray(res.data)) return res.data
    if (Array.isArray(res.data.data)) return res.data.data
  }
  if (res.status === 'OK' && Array.isArray(res.data)) return res.data
  if (Array.isArray(res.data)) return res.data
  if (res.data && Array.isArray(res.data.data)) return res.data.data
  return []
}

function matchesRouteKey(user, routeKey) {
  if (!user || routeKey == null || routeKey === '') return false
  const key = String(routeKey).trim()
  if (!key) return false

  if (user.id != null && String(user.id) === key) return true

  const emp = user.emp_id != null ? String(user.emp_id).trim() : ''
  if (emp && (emp === key || emp.toLowerCase() === key.toLowerCase())) return true

  const keyNorm = normalizeId(key)
  const empNorm = normalizeId(user.emp_id)
  return !!(keyNorm && empNorm && keyNorm === empNorm)
}

function findUser(users, routeKey) {
  if (!Array.isArray(users)) return null
  return users.find(u => matchesRouteKey(u, routeKey)) || null
}

export default {
  components: {
    BAlert,
    BLink,
    BSpinner,
  },
  data() {
    return {
      loading: true,
      userData: null,
      loadError: null,
    }
  },
  computed: {
    routeKey() {
      return this.$route.params.id
    },
  },
  watch: {
    routeKey: {
      immediate: true,
      handler() {
        this.loadUser()
      },
    },
  },
  methods: {
    async loadUser() {
      this.loading = true
      this.loadError = null
      this.userData = null

      const key = this.routeKey
      if (key == null || key === '') {
        this.userData = undefined
        this.loading = false
        return
      }

      try {
        let users = []

        const listRes = await userService.getUserList()
        if (listRes && (listRes.status === 401 || listRes.status === 403)) {
          localStorage.clear()
          this.$router.push({ name: 'auth-login' })
          return
        }
        if (listRes && listRes.status === 200) {
          users = extractUserArray(listRes)
        }

        let user = findUser(users, key)

        if (!user) {
          const { users: allUsers } = await fetchOrgChartUsers()
          if (allUsers.length) {
            users = allUsers
            user = findUser(users, key)
          }
        }

        if (!user) {
          this.userData = undefined
          return
        }

        this.userData = user
        this.$router.replace({ name: 'apps-users-list' })
      } catch (err) {
        console.error('[UsersView] loadUser failed', err)
        this.loadError = 'Something went wrong while loading this user. Please try again or contact an administrator.'
        this.userData = undefined
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.user-view-page__state {
  padding: 3rem 1rem;
}
</style>
